import { useState } from 'react'
import { BusyButton } from '../components/BusyButton'
import type { CSSProperties, FormEvent } from 'react'
import { ApiError, UnauthorizedError } from '@yoreflix/core/api/errors'
import { useUploadAuth } from './UploadAuthProvider'
import { PasswordField, TextField } from '@yoreflix/core/components/TextField'

/**
 * The sign-in surface, and its sibling refusal screen.
 *
 * Two DIFFERENT failures live here and must not be collapsed into one message:
 *   - "that isn't a valid login"  -> wrong credentials; try again.
 *   - "you're signed in, but this portal isn't yours" -> a real Yoreflix account (a viewer, say)
 *     with no `provider.` permission. Retyping the password will never fix it, so saying
 *     "sign-in failed" would send them round a loop they cannot exit.
 *
 * ★ The audience here is EXTERNAL — a supplier, not a colleague — so the refusal names who to
 * contact instead of leaving them to guess. There is no self-service anything in this portal:
 * accounts are provisioned by admin, always.
 */

const page: CSSProperties = {
  minHeight: '100%',
  display: 'grid',
  placeItems: 'center',
  padding: 24,
  background: 'var(--background)',
}

const card: CSSProperties = {
  width: 'min(380px, 100%)',
  background: 'var(--card)',
  color: 'var(--card-foreground)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  padding: 28,
  boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)',
}

const heading: CSSProperties = { fontSize: 'var(--fs-heading)', fontWeight: 600, margin: '0 0 4px' }
const sub: CSSProperties = { color: 'var(--muted-foreground)', fontSize: 'var(--fs-sm)', margin: '0 0 20px' }
const label: CSSProperties = { display: 'block', fontSize: 'var(--fs-sm)', fontWeight: 500, marginBottom: 6 }

const input: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '9px 11px',
  fontSize: 'var(--fs-base)',
  fontFamily: 'var(--font-standard)',
  background: 'var(--input-bg)',
  color: 'var(--input-fg)',
  border: '1px solid var(--input)',
  borderRadius: 'var(--radius)',
  marginBottom: 14,
}

const cta: CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  fontSize: 'var(--fs-base)',
  fontWeight: 600,
  fontFamily: 'var(--font-standard)',
  color: 'var(--primary-foreground)',
  border: '1px solid var(--primary)',
  borderRadius: 'var(--radius)',
  cursor: 'pointer',
}

const errorBox: CSSProperties = {
  background: 'var(--destructive-hint)',
  border: '1px solid var(--destructive)',
  color: 'var(--destructive-dark)',
  borderRadius: 'var(--radius)',
  padding: '9px 11px',
  fontSize: 'var(--fs-sm)',
  marginBottom: 14,
}

const ghost: CSSProperties = {
  border: '1px solid var(--border)',
  color: 'var(--foreground)',
  borderRadius: 'var(--radius)',
  padding: '8px 12px',
  fontSize: 'var(--fs-sm)',
  cursor: 'pointer',
}

export function SignIn() {
  const { signIn } = useUploadAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await signIn(email, password)
      // On success the provider flips status and this screen unmounts — nothing to do here.
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        // Deliberately does NOT say which of the two was wrong: that is an account-enumeration
        // oracle, and it helps an honest typo not at all.
        setError('That email and password combination was not recognised.')
      } else if (err instanceof ApiError && err.code === 429) {
        setError('Too many attempts. Wait a moment and try again.')
      } else {
        setError('Could not reach the Yoreflix API. Check your connection and try again.')
      }
      setBusy(false)
    }
  }

  return (
    <div style={page}>
      <form style={card} onSubmit={submit}>
        <h1 style={heading}>Yoreflix</h1>
        <p style={sub}>Upload sign-in</p>

        {error && <div style={errorBox} role="alert">{error}</div>}

        <label style={label} htmlFor="email">
          Email
        </label>
        <TextField
          id="email"
          style={input}
          type="email"
          value={email}
          autoComplete="username"
          autoFocus
          required
          onChange={(e) => setEmail(e.target.value)}
        />

        <label style={label} htmlFor="password">
          Password
        </label>
        <PasswordField
          id="password"
          style={input}
          value={password}
          autoComplete="current-password"
          required
          onChange={(e) => setPassword(e.target.value)}
        />

        {/* Sign-in already tracked `busy`; it just never showed anything moving. */}
        <button
          className="admin-btn-primary"
          type="submit"
          style={{ ...cta, opacity: busy ? 0.75 : 1, cursor: busy ? 'progress' : 'pointer' }}
          disabled={busy}
          aria-busy={busy}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
            {busy && <span className="dash-spinner" aria-hidden="true" />}
            {busy ? 'Signing in…' : 'Sign in'}
          </span>
        </button>
      </form>
    </div>
  )
}

/**
 * Signed in, but this account holds no `provider.` permission. Offer a sign-out (so a different
 * account can be used) rather than a retry, which could not help — and a way to reach us, since
 * an external supplier cannot fix this themselves and has nobody down the corridor to ask.
 */
export function NoPortalAccess({ email, onSignOut }: { email: string; onSignOut: () => void }) {
  return (
    <div style={page}>
      <div style={card}>
        <h1 style={heading}>No upload access</h1>
        <p style={sub}>
          You are signed in as <strong>{email}</strong>, but this account does not have access to the upload
          portal. If you think it should, email{' '}
          <a href="mailto:info@yoreflix.com" style={{ color: 'var(--primary)' }}>info@yoreflix.com</a> and we will
          set it up.
        </p>
        {/* ⚠ Sign-out is a ~3s round trip. It must acknowledge the press immediately. */}
        <BusyButton className="admin-btn" style={ghost} onClick={onSignOut} busyLabel="Signing out…">
          Sign in as someone else
        </BusyButton>
      </div>
    </div>
  )
}
